import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import pa from './ProductActivityPage.module.css'
import { useRefetch } from '../lib/connection'
import styles from './GroupsPage.module.css'
import o from '../components/Overlay.module.css'
import { useAuth } from '../contexts/AuthContext'
import { apiJson, ApiError } from '../lib/api'
import { useHelpPref } from '../lib/preferences'
import { PEOPLE_TABS, PairTabs } from '../components/PairTabs'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { fileTask, useTasks } from '../components/TaskCentre'
import { useLayer } from '../components/layers'
import { SkeletonRows, PendingButton, useSlow, inertClass, Failed } from '../components/pending'
import { rangeLabel } from './wages/format'
import { openableRow } from '../lib/openableRow'
import ps from './PersonnelPage.module.css'
import { GroupBulkPanel } from './GroupBulkPanel'

// Groups (CONTROL V4 "Groups", handoff §7c). A group is who a person is
// sorted under for pay and reporting; every worker is in exactly one, and the
// default group is where anyone without a group of their own lands. The list
// opens a slide-out panel whose draft (name, manager, removals, archived) is
// saved together. Members are read-and-remove here, not add (§7c): a
// person's group is set on the person, in Personnel or on import.
//
// "Where their data goes" holds Timesheet capture and the group's
// destinations. Capture off takes the group's people off Timesheets and Pay
// Workers on the server, not just on screen (lib/timesheetCapture.js). V4's
// "#id" and "kind" lines are left out: there is no group number, and §7c
// forces no meaning on a group.

export interface Group {
  id: string
  name: string
  is_default: boolean
  is_archived: boolean
  manager_membership_id: string | null
  manager_email: string | null
  manager_name: string | null
  manager_not_reporting: boolean
  member_count: number
  timesheet_capture: boolean
  destinations?: Destination[]
}
// What switching capture off would take off Timesheets in the tenant's current
// pay period (GET /api/groups/:id/capture-impact, 087).
interface CaptureImpact {
  from: string
  to: string
  workers: number
  shifts: number
  recorded_minutes: number
  open_sessions: number
}
// A registry entry (apps/api/src/lib/groupDestinations.js) with the state of
// the integration it rides on; on a group, also its own last send.
export interface Destination {
  id: string
  label: string
  provider: string | null
  status: 'connected' | 'not-connected' | 'error'
  error: string | null
  last_sent_at?: string | null
  // The latest push to this destination with this group in it (107).
  last_attempt?: { outcome: 'sent' | 'failed' | 'unknown'; code: string | null; message: string | null; at: string } | null
  // The latest export or push routed by this destination that named this
  // group, and what it did for the group (h 1205).
  last_run?: {
    outcome: 'file_produced' | 'nothing_included' | 'pushed' | 'push_unknown' | 'push_failed'
    workers: number
    period_from: string
    period_to: string
    at: string
  } | null
}
// §7c: provider tags link to that integration's card; CSV export is a label.
export const integrationAnchor = (provider: string) => `/integrations#integration-${provider.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
const sentAt = (iso: string) =>
  new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
// The time is written by the Pay Workers export that put the group in a file
// or the push that was accepted (§7c L1160). CSV export is a file someone
// downloads, so it says exported; a provider destination says sent.
const lastSendLabel = (id: string, at?: string | null) =>
  id === 'csv_export' ? (at ? `Last exported ${sentAt(at)}` : 'Not exported yet') : at ? `Last sent ${sentAt(at)}` : 'Not sent yet'
// A push that did not go through, on the tag and in the panel (h 1206-1207).
// It stays until a later push with this group in it is accepted.
export function attemptProblem(d: Destination): string | null {
  const a = d.last_attempt
  if (!a || a.outcome === 'sent') return null
  if (a.outcome === 'failed') return `The last push to ${d.label}, ${sentAt(a.at)}, failed: ${a.message || 'it was not accepted'}`
  return `The last push to ${d.label}, ${sentAt(a.at)}, got no answer, so whether it arrived is unknown. Check ${d.label} before pushing that period again.`
}

const day = (ymd: string) => new Date(`${ymd}T00:00:00`).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })
const workersN = (n: number) => `${n} worker${n === 1 ? '' : 's'}`
// The last run for this group and its result (h 1205). A push that failed or
// got no answer is already said by attemptProblem, so it is not said twice.
export function lastRunLine(d: Destination): string | null {
  const r = d.last_run
  if (!r) return null
  const head = `Last run ${sentAt(r.at)}, pay period ${day(r.period_from)} to ${day(r.period_to)}: `
  if (r.outcome === 'file_produced') return `${head}${workersN(r.workers)} in the file.`
  if (r.outcome === 'pushed') return `${head}${workersN(r.workers)} pushed and accepted.`
  if (r.outcome === 'nothing_included') return `${head}nobody from this group was in it.`
  if (attemptProblem(d)) return null
  if (r.outcome === 'push_failed') return `${head}the push of ${workersN(r.workers)} failed.`
  return `${head}${workersN(r.workers)} pushed with no answer.`
}

// Terracotta tags are outbound destinations; the sage one is capture
// behaviour, a plain label because it is set on the group itself (§7c).
function DestinationTags({ list, capture }: { list: Destination[]; capture: boolean }) {
  if (!list.length && !capture) return <span className={styles.nothing}>Nothing configured</span>
  return (
    <div className={styles.tags}>
      {capture && (
        <span className={styles.tagCapture} title="Timesheet capture: set per group, not a connected provider">
          Timesheet capture
        </span>
      )}
      {list.map((d) => {
        const problem = attemptProblem(d)
        const cls = d.status === 'connected' && !problem ? styles.tagOut : styles.tagFail
        const title =
          d.status === 'error'
            ? `${d.label}: ${d.error || 'the connection has a problem'}`
            : d.status === 'not-connected'
              ? `${d.label} is no longer connected`
              : problem || undefined
        return d.provider ? (
          <Link key={d.id} className={cls} to={integrationAnchor(d.provider)} title={title} onClick={(e) => e.stopPropagation()}>
            {d.label}
          </Link>
        ) : (
          <span key={d.id} className={cls} title={title}>
            {d.label}
          </span>
        )
      })}
    </div>
  )
}
export interface Member {
  id: string
  first_name: string | null
  last_name: string | null
  preferred_name: string | null
  email: string | null
  archived?: boolean
}
interface Candidate {
  id: string
  email: string
  name: string | null
}

const HELP: [string, string][] = [
  [
    'What a group is',
    'A way of sorting your people into sets that mean something to you. Every person can sit in one group, and the group travels with them onto timesheets, wages and reports.',
  ],
  [
    'The usual reason: who pays them',
    'Direct employees in one group, each labour hire contractor in its own. If a contractor supplies and pays the crew, you need their figures reported separately and kept out of your normal payroll run. The group is what makes that split possible.',
  ],
  [
    'Or any other cut you need',
    'Skill, experience, seasons worked, RSE cohort. Nothing here forces a meaning on a group. If it is a distinction you want to filter and report on, it can be a group.',
  ],
]

type Tab = 'active' | 'archived'

export const personName = (p: Member) => [p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(' ') || p.email || 'Unnamed'
const initials = (p: Member) =>
  personName(p)
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
const hoursText = (min: number) => {
  const h = Math.floor(min / 60)
  const m = min % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

// A spreadsheet runs a cell that starts with = + - or @, so those are
// written as text.
const cell = (v: unknown) => {
  let s = v === null || v === undefined ? '' : String(v)
  if (/^[=+\-@]/.test(s)) s = "'" + s
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}
export function groupsCsv(groups: Group[]) {
  const lines = [['Group', 'Default', 'Archived', 'Members', 'Manager', 'Manager email'].join(',')]
  for (const g of groups) {
    lines.push([g.name, g.is_default ? 'Yes' : 'No', g.is_archived ? 'Yes' : 'No', g.member_count, g.manager_name || '', g.manager_email || ''].map(cell).join(','))
  }
  return lines.join('\r\n') + '\r\n'
}

export function GroupsPage() {
  const { user } = useAuth()
  const [groups, setGroups] = useState<Group[] | null>(null)
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('active')
  const [q, setQ] = useState('')
  const [help, setHelp] = useHelpPref(user, 'groups')
  // undefined: closed; null: a new group; otherwise the group being edited.
  const [openGroup, setOpenGroup] = useState<Group | null | undefined>(undefined)
  const [toast, setToast] = useState('')
  // Ticked rows (the Personnel pattern): one opens its own panel, two or more
  // the bulk edit.
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const { startTask } = useTasks()

  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    setError(null)
    try {
      const [g, c] = await Promise.all([
        apiJson<Group[]>(u, '/api/groups'),
        apiJson<Candidate[]>(u, '/api/groups/manager-candidates').catch(() => [] as Candidate[]),
      ])
      setGroups(g)
      setCandidates(c)
    } catch (e: any) {
      setError(e?.message || 'Could not load the groups')
    }
  }, [uid])

  useEffect(() => {
    load()
  }, [load])
  useRefetch(load)

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3200)
    return () => clearTimeout(t)
  }, [toast])

  const all = groups || []
  // The first load draws skeleton rows (h 3994-4002); the tabs and the
  // search filter what is already here, so they never wait.
  const loading = !groups && !error
  const defaultGroup = all.find((g) => g.is_default) || null
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return all
      .filter((g) => (tab === 'archived' ? g.is_archived : !g.is_archived))
      .filter((g) => !needle || [g.name, g.manager_name, g.manager_email].some((v) => v && v.toLowerCase().includes(needle)))
  }, [all, tab, q])
  const counts = { active: all.filter((g) => !g.is_archived).length, archived: all.filter((g) => g.is_archived).length }

  const changeTab = (k: Tab) => {
    setTab(k)
    setOpenGroup(undefined)
    setSelected(new Set())
  }

  const rowIds = rows.map((g) => g.id)
  const shownSelected = rowIds.filter((id) => selected.has(id)).length
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const toggleAll = () => setSelected(shownSelected === rowIds.length ? new Set() : new Set(rowIds))
  const picked = all.filter((g) => selected.has(g.id))
  const editSelection = () => {
    if (picked.length === 1) setOpenGroup(picked[0])
    else if (picked.length > 1) setBulkOpen(true)
  }
  const onBulkSaved = (message: string) => {
    setBulkOpen(false)
    setSelected(new Set())
    setToast(message)
    load()
  }

  const onSaved = (g: Group, message: string) => {
    setOpenGroup(undefined)
    setToast(message)
    // Member moves change other groups' counts (and a first create makes the
    // default group), so the list is read again rather than patched.
    setGroups((gs) => {
      const list = gs || []
      return list.some((x) => x.id === g.id) ? list.map((x) => (x.id === g.id ? g : x)) : [...list, g]
    })
    load()
  }

  if (!groups && !error) {
    return (
      <div className={styles.container}>
        <PageHeader title="Groups" actions={null} tabs={<PairTabs label="Personnel and groups" tabs={PEOPLE_TABS} current="Groups" />} />
        <PageBody>
          <p>Loading...</p>
        </PageBody>
      </div>
    )
  }

  const headerActions = (
    <>
      <button type="button" className={headerStyles.btn} aria-label="Export groups" disabled={!groups} onClick={() => {
          const shown = rows
          startTask(fileTask('Export groups', plural(shown.length, `${tab} group`, `${tab} groups`), 'groups.csv', 'text/csv;charset=utf-8', () => groupsCsv(shown)))
        }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Export
      </button>
      <button type="button" className={`${headerStyles.btn} ${headerStyles.btnPrimary}`} onClick={() => setOpenGroup(null)} disabled={!groups}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
        New group
      </button>
      <button type="button" className={headerStyles.btnHelp} onClick={() => setHelp(!help)}>
        {help ? 'Hide help' : 'Help'}
      </button>
    </>
  )

  return (
    <div className={styles.container}>
      <PageHeader title="Groups" actions={headerActions} tabs={<PairTabs label="Personnel and groups" tabs={PEOPLE_TABS} current="Groups" />} />

      <PageBody className={styles.body}>

      {help && (
        <div className={pa.helpGrid}>
          {HELP.map(([h, body]) => (
            <div key={h} className={pa.helpCard}>
              <span>{h}</span>
              <p>{body}</p>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className={pa.errorBar} role="alert">
          <span>{error}</span>
          {!groups && (
            <button type="button" className={pa.ghostSmall} onClick={load}>
              Retry
            </button>
          )}
          <button type="button" className={pa.flashX} aria-label="Dismiss" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      <div className={styles.searchRow}>
        <div className={pa.searchWrap}>
          <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" className={pa.searchIcon} aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          <input className={pa.search} placeholder="Search groups…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search groups" />
        </div>
      </div>

      {picked.length > 0 && (
        <div className={ps.selBar} aria-label="Selection">
          <span className={ps.selLabel}>{plural(picked.length, 'group selected', 'groups selected')}</span>
          {shownSelected < rowIds.length && (
            <button type="button" className={ps.selLink} onClick={() => setSelected(new Set(rowIds))}>
              Select all {rowIds.length}
            </button>
          )}
          <button type="button" className={ps.selClear} onClick={() => setSelected(new Set())}>
            Clear
          </button>
          <div className={ps.selActions}>
            <button type="button" className={ps.selMerge} onClick={editSelection}>
              {picked.length === 1 ? 'Edit group' : `Bulk edit ${picked.length}`}
            </button>
          </div>
        </div>
      )}

      <div className={pa.card}>
        <div className={pa.cardHead}>
          <div className={pa.tabTrack} role="tablist">
            {(['active', 'archived'] as Tab[]).map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? pa.tabOn : pa.tab} onClick={() => changeTab(k)}>
                {k === 'active' ? 'Active' : 'Archived'}
                <span className={pa.tabCount}>{counts[k]}</span>
              </button>
            ))}
          </div>
        </div>
        <div className={styles.table} role="table" aria-label="Groups">
          <div className={styles.headRow} role="row">
            <span role="columnheader" className={styles.checkCol}>
              <PageCheckbox checked={rowIds.length > 0 && shownSelected === rowIds.length} partial={shownSelected > 0 && shownSelected < rowIds.length} disabled={rowIds.length === 0 || loading} onChange={toggleAll} />
            </span>
            <span role="columnheader">Group</span>
            <span role="columnheader" className={styles.num}>
              Members
            </span>
            <span role="columnheader">Sends to</span>
            <span role="columnheader">Manager</span>
          </div>
          {loading ? (
            <SkeletonRows rowClass={styles.row} cols={5} label="Loading groups" />
          ) : rows.length === 0 ? (
            <div className={pa.empty}>{!groups ? 'The groups could not be loaded.' : q.trim() ? 'No group matches that search.' : tab === 'archived' ? 'No archived groups.' : 'No groups yet. Create one to get started.'}</div>
          ) : (
            rows.map((g) => (
              <div key={g.id} role="row" data-testid="group-row" className={g.is_default ? styles.rowDefault : styles.row} {...openableRow(() => setOpenGroup(g))}>
                <div className={styles.checkCol} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <input type="checkbox" aria-label={`Select ${g.name}`} checked={selected.has(g.id)} onChange={() => toggle(g.id)} />
                </div>
                <div className={styles.nameLine}>
                  <span className={styles.name}>{g.name}</span>
                  {g.is_default && <span className={styles.chipDefault}>Default</span>}
                </div>
                <span className={styles.people}>{g.member_count}</span>
                <DestinationTags list={g.destinations || []} capture={g.timesheet_capture !== false} />
                <div className={styles.mgrCell}>
                  <div className={styles.mgr}>{g.manager_name || (g.manager_membership_id ? g.manager_email : 'Unassigned')}</div>
                  <div className={styles.mgrMail}>{g.manager_membership_id ? g.manager_email : '—'}</div>
                  {g.manager_not_reporting && <div className={styles.mgrFlag}>No longer Reporting only</div>}
                </div>
              </div>
            ))
          )}
        </div>
        <div className={styles.foot}>
          <span>{plural(rows.length, `${tab} group`, `${tab} groups`)}</span>
          <span className={styles.footNote}>Every worker belongs to exactly one group</span>
        </div>
      </div>

      </PageBody>

      {openGroup !== undefined && (
        <GroupPanel
          key={openGroup?.id || 'new'}
          group={openGroup}
          groups={all}
          defaultName={defaultGroup?.name || 'the default group'}
          candidates={candidates}
          onClose={() => setOpenGroup(undefined)}
          onSaved={onSaved}
        />
      )}

      {bulkOpen && picked.length > 1 && <GroupBulkPanel groups={picked} onClose={() => setBulkOpen(false)} onSaved={onBulkSaved} />}

      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  )
}

function PageCheckbox({ checked, partial, onChange, disabled }: { checked: boolean; partial: boolean; onChange: () => void; disabled: boolean }) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial
  }, [partial])
  return <input ref={ref} type="checkbox" aria-label="Select every group shown" checked={checked} disabled={disabled} onChange={onChange} />
}

function Switch({ on, label, disabled, onClick }: { on: boolean; label: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} className={o.switch} onClick={onClick} />
  )
}

interface PanelProps {
  group: Group | null
  groups: Group[]
  defaultName: string
  candidates: Candidate[]
  onClose: () => void
  onSaved: (g: Group, message: string) => void
}

function GroupPanel({ group, groups, defaultName, candidates, onClose, onSaved }: PanelProps) {
  const { user } = useAuth()
  const isNew = !group
  const [name, setName] = useState(group?.name || '')
  const [managerId, setManagerId] = useState<string | null>(group?.manager_membership_id || null)
  const [archived, setArchived] = useState(!!group?.is_archived)
  const [members, setMembers] = useState<Member[] | null>(isNew ? [] : null)
  const [removed, setRemoved] = useState<string[]>([])
  const [comboOpen, setComboOpen] = useState(false)
  const [comboQ, setComboQ] = useState('')
  // The row the arrow keys are on: 0 is "Nobody assigned", then the matches in order; -1 is none yet.
  const [comboActive, setComboActive] = useState(-1)
  const [nameError, setNameError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  const [destOptions, setDestOptions] = useState<Destination[] | null>(null)
  const startDest = (group?.destinations || []).map((d) => d.id)
  const [dest, setDest] = useState<string[]>(startDest)
  const startCapture = group ? group.timesheet_capture !== false : true
  const [capture, setCapture] = useState(startCapture)
  const [impact, setImpact] = useState<CaptureImpact | 'loading' | { error: string } | null>(null)

  // §7c: warn on switching capture off for a group that already has hours in
  // the current period. Asked with no range, the API counts the tenant's
  // current pay period (087), the one Pay Workers opens on. Advisory only.
  useEffect(() => {
    if (!group || capture || !startCapture) {
      setImpact(null)
      return
    }
    let live = true
    setImpact('loading')
    apiJson<CaptureImpact>(user, `/api/groups/${group.id}/capture-impact`)
      .then((r) => live && setImpact(r))
      .catch((e: any) => live && setImpact({ error: e?.message || 'Could not check' }))
    return () => {
      live = false
    }
  }, [capture])

  useEffect(() => {
    if (!group) return
    let live = true
    apiJson<Group & { members: Member[] }>(user, `/api/groups/${group.id}`)
      .then((g) => live && setMembers(g.members))
      .catch((e: any) => live && setSaveError(e?.message || 'Could not load the members'))
    return () => {
      live = false
    }
  }, [group?.id])

  useEffect(() => {
    let live = true
    apiJson<Destination[]>(user, '/api/groups/destinations')
      .then((o) => live && setDestOptions(o))
      .catch((e: any) => live && setSaveError(e?.message || 'Could not load the destinations'))
    return () => {
      live = false
    }
  }, [])

  const destChanged = [...dest].sort().join() !== [...startDest].sort().join()
  const dirty =
    name !== (group?.name || '') || managerId !== (group?.manager_membership_id || null) || archived !== !!group?.is_archived || removed.length > 0 || destChanged || capture !== startCapture

  // The panel and its manager list are layers (h 425-458): Escape closes the
  // open list first, then the panel, which asks first if it holds changes.
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })
  useLayer(comboOpen, () => setComboOpen(false))

  const locked = !!group?.is_default
  const kept = (members || []).filter((m) => !removed.includes(m.id))
  const count = kept.length
  const removedPeople = (members || []).filter((m) => removed.includes(m.id))

  // The manager picker: a searchable list of Reporting only system users,
  // matched on name or email (§7c). A manager whose role has since changed
  // stays shown, flagged, until someone picks again.
  const current = managerId ? candidates.find((c) => c.id === managerId) : null
  const staleManager = managerId && !current && group?.manager_membership_id === managerId ? { name: group.manager_name, email: group.manager_email } : null
  const managerLabel = current ? [current.name, current.email].filter(Boolean).join(' · ') : staleManager ? [staleManager.name, staleManager.email].filter(Boolean).join(' · ') : ''
  const needle = comboQ.trim().toLowerCase()
  const options = candidates.filter((c) => !needle || [c.name, c.email].some((v) => v && v.toLowerCase().includes(needle)))
  const pickManager = (id: string | null) => {
    setManagerId(id)
    setComboOpen(false)
    setComboQ('')
    setComboActive(-1)
  }
  // The list is operable from the keyboard: Down and Up walk it, Enter picks the row they are on (or the first match
  // of what was typed), and Escape is the layer's. A mouse can pick any row, so the keys must reach every one too.
  const rowCount = options.length + 1
  const onComboKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!comboOpen) {
        setComboOpen(true)
        setComboQ('')
        setComboActive(0)
        return
      }
      setComboActive((i) => (e.key === 'ArrowDown' ? (i + 1) % rowCount : (i - 1 + rowCount) % rowCount))
    } else if (e.key === 'Enter' && comboOpen) {
      const at = comboActive >= 0 ? comboActive : needle && options.length ? 1 : -1
      if (at < 0) return
      e.preventDefault()
      pickManager(at === 0 ? null : options[at - 1].id)
    }
  }


  const blocked = !name.trim()
  const archiveNote = locked
    ? 'The default group can never be archived. Every worker without a group of their own lands here.'
    : archived
      ? 'Archived. Its history stays intact and its reports stop. Switch off to bring it back.'
      : count > 0
        ? `Cannot archive a group with ${plural(count, 'person', 'people')} in it. Move them to another group first.`
        : 'Stops its reports and hides it from the default list, keeping every past timesheet.'
  const archiveLocked = (locked || count > 0) && !archived

  const save = async () => {
    if (blocked || busy || (!isNew && !dirty)) return
    setBusy(true)
    setSaveError(null)
    setNameError(null)
    try {
      let saved: Group & { removed?: number }
      if (isNew) {
        saved = await apiJson(user, '/api/groups', {
          method: 'POST',
          body: {
            name: name.trim(),
            managerMembershipId: managerId,
            timesheetCapture: capture,
            ...(dest.length ? { destinations: dest } : {}),
          },
        })
      } else {
        const body: Record<string, unknown> = {}
        if (name.trim() !== group!.name) body.name = name.trim()
        if (managerId !== group!.manager_membership_id) body.managerMembershipId = managerId
        if (archived !== group!.is_archived) body.isArchived = archived
        if (removed.length) body.removeHumanIds = removed
        if (destChanged) body.destinations = dest
        if (capture !== startCapture) body.timesheetCapture = capture
        saved = await apiJson(user, `/api/groups/${group!.id}`, { method: 'PUT', body })
      }
      const parts = [isNew ? `${saved.name} created` : `${saved.name} saved`]
      if (saved.removed) parts.push(`${plural(saved.removed, 'person', 'people')} moved to ${defaultName}`)
      onSaved(saved, parts.join(' · '))
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'GROUP_NAME_TAKEN') setNameError(e.message)
      else setSaveError(e?.message || 'The group was not saved')
      setBusy(false)
    }
  }

  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.drawer} ${styles.panel}`} role="dialog" aria-label={name.trim() || (isNew ? 'New group' : group!.name)}>
        <div className={o.drawerHead}>
          <span className={isNew || archived ? styles.iconMuted : styles.icon} aria-hidden="true">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 3 9 5-9 5-9-5 9-5Z" />
              <path d="m3 13 9 5 9-5" />
            </svg>
          </span>
          <div className={o.headText}>
            <h2 className={`${o.drawerTitle} ${styles.panelTitle}`}>{isNew ? 'New group' : group!.name}</h2>
            <p className={o.drawerSub}>{isNew ? 'Nobody is in it yet. Assign people from Personnel, or on import.' : plural(group!.member_count, 'person', 'people')}</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={`${o.drawerBody} ${styles.panelBody} ${inertClass(busy, slow)}`}>
          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="gp-name">
              Group name <span className={o.req}>required</span>
            </label>
            <input
              id="gp-name"
              className={nameError ? `${o.input} ${o.inputBad}` : blocked ? `${o.input} ${styles.inputNeeded}` : o.input}
              disabled={busy}
              value={name}
              placeholder="Name this group"
              onChange={(e) => {
                setName(e.target.value)
                setNameError(null)
              }}
            />
            {nameError && (
              <span className={styles.fieldError} role="alert">
                {nameError}
              </span>
            )}
          </div>

          <div className={`${o.field} ${styles.comboField}`}>
            <label className={o.fieldLabel} htmlFor="gp-mgr">
              Group manager <span className={styles.optional}>optional</span>
            </label>
            <input
              id="gp-mgr"
              role="combobox"
              aria-expanded={comboOpen}
              aria-controls="gp-mgr-list"
              aria-autocomplete="list"
              autoComplete="off"
              className={o.input}
              disabled={busy}
              value={comboOpen ? comboQ : managerLabel}
              placeholder={comboOpen ? 'Search by name or email' : 'Nobody assigned'}
              onFocus={() => {
                setComboOpen(true)
                setComboQ('')
              }}
              aria-activedescendant={comboOpen && comboActive >= 0 && comboActive < rowCount ? `gp-mgr-opt-${comboActive}` : undefined}
              onChange={(e) => {
                setComboOpen(true)
                setComboQ(e.target.value)
                setComboActive(-1)
              }}
              onKeyDown={onComboKey}
              onBlur={() => {
                setComboOpen(false)
                setComboActive(-1)
              }}
            />
            <span className={o.fieldHint}>Only system users with the Reporting only role can be picked. Leave it empty if nobody outside the office needs this group’s reports.</span>
            {comboOpen && (
              <ul id="gp-mgr-list" role="listbox" aria-label="Group manager" className={styles.options}>
                <li
                  id="gp-mgr-opt-0"
                  role="option"
                  aria-selected={!managerId}
                  className={`${!managerId ? styles.optionOn : styles.option} ${comboActive === 0 ? styles.optionActive : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pickManager(null)
                  }}
                >
                  <span className={styles.optName}>Nobody assigned</span>
                </li>
                {options.map((c, i) => (
                  <li
                    key={c.id}
                    id={`gp-mgr-opt-${i + 1}`}
                    role="option"
                    aria-selected={c.id === managerId}
                    className={`${c.id === managerId ? styles.optionOn : styles.option} ${comboActive === i + 1 ? styles.optionActive : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      pickManager(c.id)
                    }}
                  >
                    <span className={styles.optName}>{c.name || c.email}</span>
                    {c.name && <span className={styles.optMail}>{c.email}</span>}
                  </li>
                ))}
                {options.length === 0 && <li className={styles.optNone}>{candidates.length ? 'No Reporting only user matches' : 'No system user has the Reporting only role yet'}</li>}
              </ul>
            )}
            {staleManager && (
              <span className={styles.fieldError}>
                {staleManager.name || staleManager.email} no longer has the Reporting only role. Pick someone who does, or nobody.
              </span>
            )}
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={o.label}>Members ({count})</span>
            </div>

            {members === null && <div className={styles.empty}>Loading members…</div>}
            {members !== null && count === 0 && <div className={styles.empty}>Nobody in this group yet. Set it as their group from Personnel.</div>}

            {kept.length > 0 && (
              <div className={styles.members}>
                {kept.slice(0, 4).map((m, i) => (
                  <div key={m.id} className={styles.member} data-testid="member-row">
                    <span className={styles.avatar} style={{ background: i % 2 ? '#7a8a5e' : '#c67139' }}>
                      {initials(m)}
                    </span>
                    <div className={styles.memberText}>
                      <div className={styles.memberName}>
                        {personName(m)}
                        {m.archived && <span className={styles.tag}>Archived</span>}
                      </div>
                      <div className={styles.memberMail}>{m.email || 'No email'}</div>
                    </div>
                    {!locked && (
                      <button type="button" className={styles.rowBtn} disabled={busy} aria-label={`Remove ${personName(m)}`} onClick={() => setRemoved((r) => [...r, m.id])}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {removedPeople.length > 0 && (
              <div className={styles.pending}>
                <span>
                  {removedPeople.length === 1 ? personName(removedPeople[0]) : `${personName(removedPeople[0])} and ${plural(removedPeople.length - 1, 'other', 'others')}`} move
                  {removedPeople.length === 1 ? 's' : ''} to {defaultName} when you save.
                </span>
                <button type="button" className={styles.undoBtn} disabled={busy} onClick={() => setRemoved([])}>
                  Undo
                </button>
              </div>
            )}
            {locked && count > 0 && (
              <span className={o.fieldHint}>Nobody can be removed from the default group. It is where everyone without a group of their own lands. To move someone out, set their new group from Personnel.</span>
            )}
            {group && count > 4 && (
              <Link className={styles.more} to={`/personnel?group=${group.id}`}>
                See all {count} members in Personnel
              </Link>
            )}
          </div>

          {/* §7c "Where their data goes": a pick-any list of the registry. An
              unconnected provider is listed greyed so an admin learns it
              exists; one already on stays on (and shows its failure) if its
              integration drops. Nothing sends from here. */}
          <div className={styles.section} role="group" aria-label="Where their data goes">
            <div className={styles.sectionHead}>
              <span className={o.label}>Where their data goes</span>
            </div>
            <span className={o.fieldHint}>Pick any that apply. Nothing is sent from here: a send is always done from Pay Workers.</span>
            <div className={styles.destList}>
              {/* Timesheet capture sits in the same list but sends nothing, so
                  its badge reads "On" and carries no timestamp (§7c). */}
              <label className={capture ? styles.destCapture : styles.dest}>
                <input type="checkbox" checked={capture} disabled={busy} onChange={() => setCapture(!capture)} />
                <span className={styles.destText}>
                  <span className={styles.destName}>Timesheet capture</span>
                  <span className={styles.destNote}>
                    Workers in this group clock on and off in the app. Switch it off and their picks are still recorded against them, but they will not appear in Timesheets or
                    Pay Workers.
                  </span>
                </span>
                {capture && <span className={styles.badgeOn}>On</span>}
              </label>
              {impact && <CaptureWarning impact={impact} />}
            </div>
            {!destOptions ? (
              <div className={styles.empty}>Loading…</div>
            ) : (
              <div className={styles.destList}>
                {destOptions.map((o) => {
                  const on = dest.includes(o.id)
                  const usable = o.status === 'connected' || on
                  const mine = (group?.destinations || []).find((d) => d.id === o.id)
                  return (
                    <label key={o.id} className={on ? styles.destOn : usable ? styles.dest : styles.destOff}>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={!usable || busy}
                        onChange={() => setDest(on ? dest.filter((d) => d !== o.id) : [...dest, o.id])}
                      />
                      <span className={styles.destText}>
                        <span className={styles.destName}>{o.label}</span>
                        {!usable && <span className={styles.destNote}>Not connected. Set it up in Integrations before a group can use it.</span>}
                        {on && o.status === 'error' && <span className={styles.destFail}>{o.label} has a problem: {o.error || 'check it in Integrations'}</span>}
                        {on && o.status === 'not-connected' && <span className={styles.destFail}>{o.label} is no longer connected. Reconnect it in Integrations.</span>}
                        {on && mine && lastRunLine(mine) && (
                          <span className={styles.destNote} data-testid={`dest-run-${o.id}`}>
                            {lastRunLine(mine)}
                          </span>
                        )}
                        {on && mine && attemptProblem(mine) && (
                          <span className={styles.destFail} data-testid={`dest-attempt-${o.id}`}>
                            {attemptProblem(mine)}
                          </span>
                        )}
                      </span>
                      {on && <span className={styles.destSent}>{lastSendLabel(o.id, mine?.last_sent_at)}</span>}
                    </label>
                  )
                })}
              </div>
            )}
            <span className={o.fieldHint}>
              Accounts and connections live in <Link to="/integrations">Integrations</Link>. This list only chooses which of them this group's data may go to.
            </span>
          </div>

          {!isNew && (
            <div className={archiveLocked ? `${o.toggleRow} ${styles.archLocked}` : o.toggleRow}>
              <div className={o.toggleText}>
                <div className={o.toggleTitle}>Archived</div>
                <div className={o.toggleSub}>{archiveNote}</div>
              </div>
              <Switch on={archived} label="Archived" disabled={archiveLocked || (locked && !archived) || busy} onClick={() => setArchived(!archived)} />
            </div>
          )}
        </div>

        <div className={`${o.drawerFoot} ${styles.panelFoot}`}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          {blocked && <span className={o.footNote}>Name needed</span>}
          <PendingButton
            type="button"
            className={o.primary}
            busy={busy}
            disabled={blocked || busy || (!isNew && !dirty)}
            pending={isNew ? 'Creating…' : 'Saving…'}
            title={blocked ? 'Give the group a name first' : !isNew && !dirty ? 'Nothing has changed yet' : 'Saves the group and any member changes together'}
            onClick={save}
          >
            {isNew ? 'Create group' : 'Save changes'}
          </PendingButton>
          <Failed text={saveError} />
        </div>
      </div>
      {confirm}
    </div>
  )
}

// The existing-hours warning (§7c). Hours are the clock times of closed
// sessions; an open session is counted, not timed. Nothing is deleted by
// switching capture off, and the warning says so.
function CaptureWarning({ impact }: { impact: CaptureImpact | 'loading' | { error: string } }) {
  if (impact === 'loading') return <div className={styles.captureCheck}>Checking this pay period’s hours…</div>
  if ('error' in impact) {
    return <div className={styles.captureWarn}>Could not check this pay period’s hours ({impact.error}). Switching capture off takes this group off Timesheets and Pay Workers.</div>
  }
  const period = rangeLabel(impact.from, impact.to)
  if (!impact.shifts) {
    return <div className={styles.captureCheck}>Nobody in this group has hours on Timesheets this pay period ({period}).</div>
  }
  const hours = [
    impact.recorded_minutes ? `${hoursText(impact.recorded_minutes)} recorded` : null,
    impact.open_sessions ? `${plural(impact.open_sessions, 'session', 'sessions')} still clocked on` : null,
  ]
    .filter(Boolean)
    .join(' and ')
  return (
    <div className={styles.captureWarn} role="alert" data-testid="capture-warning">
      <strong>
        {plural(impact.workers, 'person', 'people')} in this group already {impact.workers === 1 ? 'has' : 'have'} hours this pay period ({period}): {plural(impact.shifts, 'shift', 'shifts')}
        {hours ? `, ${hours}` : ''}.
      </strong>{' '}
      Switching capture off takes them off Timesheets and Pay Workers, these hours included, so anyone paid by the hour would not be paid from here. Nothing is deleted: switch it back on and they return.
    </div>
  )
}
